import { useReportLoading } from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { AiTwotoneMail } from 'react-icons/ai';
import { AppVarsContext } from '../../context/AppVarsContext';
import { lightenHexColor } from '../../dwi-functions';
import {
  EmailGallerySection,
  EmailInsightsCard,
  EmailMetricGrid,
  EmailTableSection,
  formatEmailDate,
} from '../shared/emailReportShared';
import {
  getPlainSurfaceCardSx,
  getReportBackdropSx,
  getReportCanvasSx,
} from '../shared/reportSurfaceStyles';
import { GlassLoadingState, formatReportDateRangeLabel } from '../shared/reportStatusCards';
import { formatUsd } from '../../system/report-formatters';

const safeNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(safeNum(value));
const formatCurrency = (value) => formatUsd(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(1)}%`;

const normalizeRate = (value) => {
  const num = safeNum(value);
  if (num > 0 && num <= 1) return num * 100;
  return num;
};

const readPreviewHref = (row) => (
  row?.preview_url
  || row?.previewHref
  || row?.archive_url
  || row?.web_version_url
  || row?.permalink_url
  || row?.campaign_url
  || row?.url
  || row?.raw?.preview_url
  || row?.raw?.archive_url
  || row?.raw?.web_version_url
  || row?.raw?.permalink_url
  || row?.raw?.campaign_url
  || row?.raw?.url
  || ''
);

const normalizeDeliveraRows = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.emails)
      ? payload.emails
      : Array.isArray(payload?.campaigns)
        ? payload.campaigns
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.results)
            ? payload.results
            : [];

  return rows.map((row, index) => {
    const sent = safeNum(row?.sent ?? row?.sends ?? row?.emails_sent ?? row?.recipients);
    const delivered = safeNum(row?.delivered ?? row?.emails_delivered ?? Math.max(0, sent - safeNum(row?.bounces)));
    const opens = safeNum(row?.unique_opens ?? row?.opens ?? row?.opened);
    const clicks = safeNum(row?.unique_clicks ?? row?.clicks);
    const conversions = safeNum(row?.conversions ?? row?.transactions ?? row?.gifts ?? row?.purchases ?? row?.unique_purchases);
    const revenue = safeNum(row?.revenue ?? row?.amount ?? row?.total_revenue);
    const bounces = safeNum(row?.bounces ?? row?.bounce_count);
    const unsubscribes = safeNum(row?.unsubscribes ?? row?.unsubscribe_count);
    const openRate = normalizeRate(row?.open_rate) || (delivered ? (opens / delivered) * 100 : 0);
    const clickRate = normalizeRate(row?.click_rate) || (delivered ? (clicks / delivered) * 100 : 0);
    const ctor = normalizeRate(row?.ctor) || (opens ? (clicks / opens) * 100 : 0);
    const bounceRate = normalizeRate(row?.bounce_rate) || (sent ? (bounces / sent) * 100 : 0);

    return {
      id: row?.id || row?.email_id || row?.emailId || row?.campaign_id || row?.campaignId || `delivera-email-${index}`,
      name: row?.name || row?.subject || row?.email_name || row?.campaign_name || `Delivra Email ${index + 1}`,
      sendDate: row?.send_date || row?.sent_at || row?.send_time || row?.created_at || row?.createdAt,
      previewText: row?.preview_text || row?.preheader || row?.description || '',
      sent,
      delivered,
      opens,
      clicks,
      conversions,
      revenue,
      bounces,
      unsubscribes,
      openRate,
      clickRate,
      ctor,
      bounceRate,
      raw: row,
    };
  });
};

const normalizeGroupText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const buildDeliveraGroupKey = (row) => {
  const name = normalizeGroupText(row?.name || row?.raw?.Title);
  const subject = normalizeGroupText(row?.raw?.subject || row?.raw?.Subject);
  const type = normalizeGroupText(row?.raw?.type || row?.raw?.Type);
  const listname = normalizeGroupText(row?.raw?.listname || row?.raw?.List);
  const sendDate = row?.sendDate ? new Date(row.sendDate) : null;
  const sendKey = sendDate && !Number.isNaN(sendDate.getTime())
    ? sendDate.toISOString().slice(0, 16)
    : '';

  return [
    name || subject || 'delivra-email',
    subject || name || 'no-subject',
    type,
    listname,
    sendKey,
  ].filter(Boolean).join('::');
};

const groupDeliveraRows = (rows) => {
  const groups = new Map();

  rows.forEach((row) => {
    const key = buildDeliveraGroupKey(row);
    const rowTime = new Date(row?.sendDate || 0).getTime() || 0;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        ...row,
        id: key,
        childCount: 1,
        latestTime: rowTime,
      });
      return;
    }

    existing.sent += row.sent;
    existing.delivered += row.delivered;
    existing.opens += row.opens;
    existing.clicks += row.clicks;
    existing.conversions += row.conversions;
    existing.revenue += row.revenue;
    existing.bounces += row.bounces;
    existing.unsubscribes += row.unsubscribes;
    existing.childCount += 1;

    if (rowTime >= existing.latestTime) {
      existing.latestTime = rowTime;
      existing.sendDate = row.sendDate || existing.sendDate;
      existing.previewHref = row.previewHref || existing.previewHref;
      existing.captureUrl = row.captureUrl || existing.captureUrl;
      existing.directImageUrl = row.directImageUrl || existing.directImageUrl;
      existing.raw = row.raw || existing.raw;
    }

    if (!existing.previewHref) existing.previewHref = readPreviewHref(row);
    if (!existing.captureUrl) existing.captureUrl = readPreviewHref(row);
    if (!existing.directImageUrl) existing.directImageUrl = row.raw?.thumbnail_url || row.raw?.preview_image || '';

    const existingPreview = String(existing.previewText || '');
    const nextPreview = String(row.previewText || '');
    if (!existingPreview || nextPreview.length > existingPreview.length) {
      existing.previewText = nextPreview;
    }
  });

  return Array.from(groups.values())
    .map((row) => {
      const openRate = row.delivered ? (row.opens / row.delivered) * 100 : 0;
      const clickRate = row.delivered ? (row.clicks / row.delivered) * 100 : 0;
      const ctor = row.opens ? (row.clicks / row.opens) * 100 : 0;
      const bounceRate = row.sent ? (row.bounces / row.sent) * 100 : 0;
      const consolidationNote = row.childCount > 1 ? `Consolidated from ${row.childCount} Delivra rows.` : '';

      return {
        ...row,
        openRate,
        clickRate,
        ctor,
        bounceRate,
        previewHref: row.previewHref || readPreviewHref(row),
        captureUrl: row.captureUrl || readPreviewHref(row),
        directImageUrl: row.directImageUrl || row.raw?.thumbnail_url || row.raw?.preview_image || '',
        previewText: [row.previewText, consolidationNote].filter(Boolean).join(' '),
      };
    })
    .sort((a, b) => (b.latestTime || 0) - (a.latestTime || 0));
};

export default function DeliveraReport(props) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
    useReportLoading(loading, props.setLoading);
  const [account, setAccount] = useState(null);
  const [apiTotals, setApiTotals] = useState(null);
  const [cid] = useState(sessionStorage.getItem('rid'));
  const theme = useTheme();
  const { appVars, appLoading } = useContext(AppVarsContext);
  const title = props?.title || 'Delivra';
  const dateRangeLabel = formatReportDateRangeLabel(sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate'));

  const fetchEmails = useCallback(async () => {
    if (!appVars?.api_url || !cid) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${appVars.api_url}/delivra/email/stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cid,
          sdate: sessionStorage.getItem('sDate'),
          edate: sessionStorage.getItem('eDate'),
        }),
      });
      const data = await res.json();
      setAccount(data?.account || null);
      setApiTotals(data?.totals || null);
      setEmails(normalizeDeliveraRows(data));

    } catch (error) {
      console.error('Error fetching Delivra emails:', error);
      setAccount(null);
      setApiTotals(null);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [appVars?.api_url, cid]);

  useEffect(() => {
    if (!appLoading) {
      fetchEmails();
    }
  }, [appLoading, fetchEmails]);

  useEffect(() => {
    const handleStorageChange = () => {
      setTimeout(() => fetchEmails(), 100);
    };

    window.addEventListener('dc', handleStorageChange);
    return () => window.removeEventListener('dc', handleStorageChange);
  }, [fetchEmails]);

  const groupedEmails = useMemo(() => groupDeliveraRows(emails), [emails]);

  const summary = useMemo(() => {
    const fallbackTotals = groupedEmails.reduce((acc, row) => {
      acc.sent += row.sent;
      acc.delivered += row.delivered;
      acc.opens += row.opens;
      acc.clicks += row.clicks;
      acc.conversions += row.conversions;
      acc.revenue += row.revenue;
      acc.bounces += row.bounces;
      acc.unsubscribes += row.unsubscribes;
      return acc;
    }, {
      sent: 0,
      delivered: 0,
      opens: 0,
      clicks: 0,
      conversions: 0,
      revenue: 0,
      bounces: 0,
      unsubscribes: 0,
    });

    const totals = {
      sent: safeNum(apiTotals?.emails_sent) || fallbackTotals.sent,
      delivered: safeNum(apiTotals?.delivered) || fallbackTotals.delivered,
      opens: safeNum(apiTotals?.unique_opens ?? apiTotals?.opens) || fallbackTotals.opens,
      clicks: safeNum(apiTotals?.unique_clicks ?? apiTotals?.clicks) || fallbackTotals.clicks,
      conversions: safeNum(apiTotals?.purchases ?? apiTotals?.unique_purchases ?? apiTotals?.conversions) || fallbackTotals.conversions,
      revenue: safeNum(apiTotals?.revenue) || fallbackTotals.revenue,
      bounces: safeNum(apiTotals?.bounces) || fallbackTotals.bounces,
      unsubscribes: safeNum(apiTotals?.unsubscribes) || fallbackTotals.unsubscribes,
    };

    return {
      emails: groupedEmails.length,
      totals,
      openRate: totals.delivered ? (totals.opens / totals.delivered) * 100 : 0,
      clickRate: totals.delivered ? (totals.clicks / totals.delivered) * 100 : 0,
      ctor: totals.opens ? (totals.clicks / totals.opens) * 100 : 0,
      bounceRate: totals.sent ? (totals.bounces / totals.sent) * 100 : 0,
    };
  }, [apiTotals, groupedEmails]);

  const ranked = useMemo(() => ({
    topOpen: [...groupedEmails].sort((a, b) => b.openRate - a.openRate)[0] || null,
    topClick: [...groupedEmails].sort((a, b) => b.clickRate - a.clickRate)[0] || null,
    topConversions: [...groupedEmails].sort((a, b) => b.conversions - a.conversions)[0] || null,
    topRevenue: [...groupedEmails].sort((a, b) => b.revenue - a.revenue)[0] || null,
    topBounce: [...groupedEmails].sort((a, b) => b.bounceRate - a.bounceRate)[0] || null,
  }), [groupedEmails]);

  const insightItems = useMemo(() => {
    const items = [];

    if (ranked.topRevenue?.revenue > 0) {
      items.push({
        tone: 'success',
        label: 'Top Revenue Email',
        text: `${ranked.topRevenue.name} drove ${formatCurrency(ranked.topRevenue.revenue)} in attributed revenue.`,
      });
    }

    if (ranked.topConversions?.conversions > 0) {
      items.push({
        tone: 'success',
        label: 'Top Conversion Email',
        text: `${ranked.topConversions.name} delivered ${formatNumber(ranked.topConversions.conversions)} tracked conversions.`,
      });
    }

    if (ranked.topBounce && ranked.topBounce.bounceRate >= 5) {
      items.push({
        tone: 'error',
        label: 'Deliverability Risk',
        text: `${ranked.topBounce.name} bounced at ${formatPercent(ranked.topBounce.bounceRate)}, which is the clearest deliverability issue in the selected range.`,
      });
    }

    if (summary.clickRate < 1 && summary.totals.delivered > 0) {
      items.push({
        tone: 'warning',
        label: 'CTA Weakness',
        text: `Overall click rate is only ${formatPercent(summary.clickRate)}. Opens are happening, but the call to action is not pulling enough traffic through.`,
      });
    }

    if (!items.length) {
      items.push({
        tone: 'default',
        label: 'Stable Range',
        text: 'Delivra performance stayed steady with no major engagement issues in this period.',
      });
    }

    return items.slice(0, 4);
  }, [ranked, summary.clickRate, summary.totals.delivered]);

  const metricItems = useMemo(() => ([
    { label: 'Emails', value: formatNumber(summary.emails), helper: `${formatNumber(emails.length)} raw report rows consolidated` },
    { label: 'Sent', value: formatNumber(summary.totals.sent), helper: 'Delivra sends' },
    { label: 'Delivered', value: formatNumber(summary.totals.delivered), helper: 'Successful deliveries' },
    { label: 'Opens', value: formatNumber(summary.totals.opens), helper: `Open Rate ${formatPercent(summary.openRate)}` },
    { label: 'Clicks', value: formatNumber(summary.totals.clicks), helper: `Click Rate ${formatPercent(summary.clickRate)}` },
    { label: 'Conversions', value: formatNumber(summary.totals.conversions), helper: 'Tracked outcomes' },
    { label: 'Revenue', value: formatCurrency(summary.totals.revenue), helper: 'Attributed value' },
    { label: 'Bounce Rate', value: formatPercent(summary.bounceRate), helper: `${formatNumber(summary.totals.bounces)} bounces` },
  ]), [emails.length, summary]);

  const galleryRows = useMemo(() => groupedEmails.map((row) => ({
    id: row.id,
    name: row.name,
    sendDate: row.sendDate,
    previewText: row.previewText,
    previewHref: row.previewHref || readPreviewHref(row),
    captureUrl: row.captureUrl || readPreviewHref(row),
    directImageUrl: row.directImageUrl || row.raw?.thumbnail_url || row.raw?.preview_image || '',
    thumbRequest: row.raw?.thumbRequest || null,
    placeholder: 'DE',
    metricItems: [
      { label: 'Sent', value: formatNumber(row.sent) },
      { label: 'Opens', value: `${formatNumber(row.opens)} · ${formatPercent(row.openRate)}` },
      { label: 'Clicks', value: `${formatNumber(row.clicks)} · ${formatPercent(row.clickRate)}` },
      { label: 'Revenue', value: formatCurrency(row.revenue) },
    ],
  })), [groupedEmails]);

  const sortedRows = useMemo(() => [...groupedEmails].sort((a, b) => {
    const aTime = new Date(a.sendDate).getTime() || 0;
    const bTime = new Date(b.sendDate).getTime() || 0;
    return bTime - aTime;
  }), [groupedEmails]);

  if (loading) {
    return (
      <Box sx={getReportCanvasSx(theme)}>
        <Box sx={getReportBackdropSx(theme)} />
        <GlassLoadingState
          icon={<AiTwotoneMail size={34} />}
          title={`Loading ${title}`}
          subtitle={dateRangeLabel ? `Pulling send, click, conversion, and revenue data for ${dateRangeLabel}.` : 'Pulling send, click, conversion, and revenue data for the selected range.'}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, flex: 1 }}>
      <Box sx={getReportCanvasSx(theme)}>
        <Box sx={getReportBackdropSx(theme)} />

        {groupedEmails.length > 0 ? (
          <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
            <EmailMetricGrid items={metricItems} />

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' } }}>
              <Card sx={getPlainSurfaceCardSx(theme)}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Email Engagement Funnel
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                        {account?.listname
                          ? `Delivery-to-click performance for ${account.listname}.`
                          : 'The same delivery-to-click summary used across the email reports.'}
                      </Typography>
                    </Box>

                    {[
                      { label: 'Delivered', value: summary.totals.delivered, denom: summary.totals.sent, color: theme.palette.primary.main },
                      { label: 'Opened', value: summary.totals.opens, denom: summary.totals.delivered || summary.totals.sent, color: theme.palette.secondary.main },
                      { label: 'Clicked', value: summary.totals.clicks, denom: summary.totals.delivered || summary.totals.sent, color: theme.palette.success.main },
                    ].map((item) => {
                      const percent = item.denom ? (item.value / item.denom) * 100 : 0;
                      return (
                        <Box key={item.label} sx={{ display: 'grid', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2">{item.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatNumber(item.value)} · {formatPercent(percent)}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: theme.palette.action.hover }}>
                            <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>

              <EmailInsightsCard
                title="What Needs Attention"
                subtitle="Delivra-specific engagement, conversion, and deliverability callouts."
                items={insightItems}
              />
            </Box>

            <EmailGallerySection
              providerLabel="Delivra"
              title="Emails In Range"
              subtitle="Grouped email sends for the selected date range, with Delivra segment and split rows rolled into one card where possible."
              rows={galleryRows}
            />

            <EmailTableSection
              title="Email Performance"
              subtitle="Grouped delivery, engagement, conversions, and revenue detail for each logical Delivra email send."
              rows={sortedRows}
              emptyMessage="No Delivra emails were found in the selected date range."
              columns={[
                {
                  key: 'name',
                  label: 'Email',
                  render: (row) => (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatEmailDate(row.sendDate)}
                      </Typography>
                    </Box>
                  ),
                },
                { key: 'childCount', label: 'Rows', align: 'right', render: (row) => formatNumber(row.childCount) },
                { key: 'sent', label: 'Sent', align: 'right', render: (row) => formatNumber(row.sent) },
                { key: 'openRate', label: 'Open Rate', align: 'right', render: (row) => formatPercent(row.openRate) },
                { key: 'clickRate', label: 'Click Rate', align: 'right', render: (row) => formatPercent(row.clickRate) },
                { key: 'conversions', label: 'Conversions', align: 'right', render: (row) => formatNumber(row.conversions) },
                { key: 'revenue', label: 'Revenue', align: 'right', render: (row) => formatCurrency(row.revenue) },
              ]}
            />
          </Box>
        ) : (
          <Box
            sx={{
              m: 'auto',
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              background: lightenHexColor(theme.palette.primary.main, 80),
            }}
          >
            <Box display="flex" flexDirection="column" p={2} alignItems="center" justifyContent="center" width="100%" gap={1}>
              <AiTwotoneMail size={70} color={theme.palette.primary.main} />
              <Typography>No Emails</Typography>
              <Typography>{sessionStorage.getItem('sDate')} and {sessionStorage.getItem('eDate')}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
